import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { TranslationService, Language } from '../../../services/translation.service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule, SelectModule, FormsModule],
  template: `
    @if (translationEnabled) {
      <div class="language-selector-container">
        @if (displayLanguages.length > 0) {
          <p-select
            [options]="displayLanguages"
            [(ngModel)]="selectedLanguage"
            optionLabel="displayName"
            optionValue="code"
            placeholder="Select Language"
            (onChange)="onLanguageChange($event)"
            [showClear]="false">
          </p-select>
        } @else {
          <div class="loading-placeholder">Loading...</div>
        }
      </div>
    }
  `,
  styleUrl: './language-selector.component.scss'
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  @Input() translationEnabled: boolean = false;

  languages: Language[] = [];
  displayLanguages: Array<Language & { displayName: string }> = [];
  selectedLanguage: string = 'en';
  private languageChangeListener?: EventListener;

  constructor(
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef
  ) {
    this.languages = this.translationService.supportedLanguages;
    this.selectedLanguage = this.translationService.getCurrentLanguage();
    this.updateDisplayLanguages();
  }

  ngOnInit(): void {
    this.languageChangeListener = ((event: CustomEvent) => {
      const languageCode = event.detail?.languageCode;
      if (languageCode !== undefined && this.selectedLanguage !== languageCode) {
        this.selectedLanguage = languageCode;
        this.updateDirection(languageCode);
        this.updateDisplayLanguages();
      } else if (languageCode === this.selectedLanguage) {
        this.updateDisplayLanguages();
      }
    }) as EventListener;
    window.addEventListener('languageChanged', this.languageChangeListener);
    this.updateDisplayLanguages();
    
    const currentLang = this.translationService.getCurrentLanguage();
    this.selectedLanguage = currentLang;
    this.updateDirection(this.selectedLanguage);
  }

  ngOnDestroy(): void {
    if (this.languageChangeListener) {
      window.removeEventListener('languageChanged', this.languageChangeListener);
    }
  }

  private updateDisplayLanguages(): void {
    const currentLang = this.translationService.getCurrentLanguage();
    
    if (currentLang === 'en') {
      this.displayLanguages = [...this.languages.map(lang => ({
        ...lang,
        displayName: lang.name
      }))];
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    } else {
      this.displayLanguages = this.languages.map(lang => ({
        ...lang,
        displayName: lang.name
      }));
      
      const languageNames = this.languages.map(l => l.name);
      this.translationService.translateBatch(languageNames, currentLang).subscribe(translations => {
        this.displayLanguages = this.languages.map((lang, index) => ({
          ...lang,
          displayName: translations[index] || lang.name
        }));
        this.cdr.detectChanges();
      });
    }
  }

  onLanguageChange(event: any): void {
    const languageCode = event.value;
    if (!languageCode) return;
    
    this.selectedLanguage = languageCode;
    this.translationService.setCurrentLanguage(languageCode);
    
    if (languageCode === 'en') {
      this.updateDisplayLanguages();
      setTimeout(() => {
        this.selectedLanguage = 'en';
        this.cdr.detectChanges();
        setTimeout(() => {
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        }, 50);
      }, 0);
    }
    
    window.dispatchEvent(new CustomEvent('languageChanged', { 
      detail: { languageCode } 
    }));

    this.updateDirection(languageCode);
  }

  private updateDirection(languageCode: string): void {
    this.translationService.applyDocumentLanguage(languageCode);
  }
}

